/* eslint-disable react/no-this-in-sfc */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useMemo } from 'react';
import { DateRange, DefinedRange } from 'react-date-range';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import {
  STAY_DATE_END_DATE,
  STAY_DATE_START_DATE,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import variants from 'modules/common/theme/variants';
import { selectAppTheme, selectTargetDate } from 'modules/dashboard/selectors';
import { Box } from '@mui/material';
import { endOfDay, format, isSameDay, subDays } from 'date-fns';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';

/**
 * SearchBookingDateRangePicker
 *
 * Renders a date range picker for selecting booking dates in search searchScenarios.
 * Updates the corresponding scenario filter when the date range changes.
 *
 * @param {Object} props.filterData - Filter info for the selected scenario and visual.
 * @returns {JSX.Element} Date range picker for booking dates
 */
const SearchBookingDateRangePicker = ({ filterData, found }) => {
  const dispatch = useDispatch();
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);
  const themeName = useSelector(selectAppTheme);
  const targetDate = useSelector(selectTargetDate);

  // Get the currently selected theme variant for coloring the picker
  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.name === themeName),
    [themeName]
  );
  // Handle date range selection and update scenario filter
  const handleChange = (item) => {
    if (!found) return;
    const formattedDateRange = `${format(
      item.selection.startDate,
      YEAR_MONTH_DAY_PATTERN
    )} - ${format(item.selection.endDate, YEAR_MONTH_DAY_PATTERN)}`;
    //
    if (searchScenarios?.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;

        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: formattedDateRange,
                    value: {
                      startDate: item.selection.startDate,
                      endDate: item.selection.endDate,
                    },
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );

            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }

      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    //
    if (favoriteScenarios?.length) {
      const updatedFavoriteScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;

        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: formattedDateRange,
                    value: {
                      startDate: item.selection.startDate,
                      endDate: item.selection.endDate,
                    },
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );

            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedFavoriteScenarios?.find(
          (e) => e.id === filterData?.scenarioId
        );
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedFavoriteScenarios));
    }
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: formattedDateRange,
                    value: {
                      startDate: item.selection.startDate?.toISOString(),
                      endDate: item.selection.endDate?.toISOString(),
                    },
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );

            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });

      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
  };

  const dateRange = useMemo(
    () => [
      {
        startDate: found?.value?.startDate ? new Date(found.value.startDate) : new Date(),
        endDate: found?.value?.endDate ? new Date(found.value.endDate) : new Date(),
        key: 'selection',
        color: selectedVariant?.palette.primary.main,
      },
    ],
    [found, selectedVariant]
  );

  return (
    <Box sx={{ minWidth: '600px', minHeight: '420px', bgcolor: 'transparent' }}>
      <DefinedRange
        onChange={handleChange}
        ranges={dateRange}
        className={styles.calendarElement3}
        inputRanges={[]}
        staticRanges={[
          {
            label: 'Trailing 7 days',
            range: () => ({
              startDate: subDays(endOfDay(new Date(targetDate.endDate)), 7),
              endDate: subDays(endOfDay(new Date(targetDate.endDate)), 1),
            }),
            isSelected(range) {
              const definedRange = this.range();
              return (
                isSameDay(range.startDate, definedRange.startDate) &&
                isSameDay(range.endDate, definedRange.endDate)
              );
            },
          },
          {
            label: 'Trailing 14 days',
            range: () => ({
              startDate: subDays(endOfDay(new Date(targetDate.endDate)), 14),
              endDate: subDays(endOfDay(new Date(targetDate.endDate)), 1),
            }),
            isSelected(range) {
              const definedRange = this.range();
              return (
                isSameDay(range.startDate, definedRange.startDate) &&
                isSameDay(range.endDate, definedRange.endDate)
              );
            },
          },
        ]}
      />
      <DateRange
        id="search-stay-date-range-picker"
        onChange={handleChange}
        editableDateInputs
        moveRangeOnFirstSelection={false}
        ranges={dateRange}
        months={1}
        fixedHeight
        direction="horizontal"
        className={styles.calendarElement}
        maxDate={new Date(STAY_DATE_END_DATE)}
        minDate={new Date(STAY_DATE_START_DATE)}
        dateDisplayFormat="yyyy-MM-dd"
      />
    </Box>
  );
};

export default SearchBookingDateRangePicker;
